import * as React from "react";
import { useResponsive } from "../hooks/useResponsive";
import { REFINED } from "../components/risikoanalyseStep/tokens";
import {
  PROFILE_SECTIONS,
  type ProfileSectionId,
  type ProfileSectionProps,
} from "./profile/profileShell";
import ProfileHeader from "./profile/ProfileHeader";
import ProfileTabs from "./profile/ProfileTabs";
import ProfileStammdaten from "./profile/ProfileStammdaten";
import ProfileNotifications from "./profile/ProfileNotifications";
import ProfileTermine from "./profile/ProfileTermine";
import ProfileAbwesenheiten from "./profile/ProfileAbwesenheiten";

/**
 * Profil-Seite (`/profil`) — GitHub-artige Shell: Header + sticky Tabs + vier
 * gestapelte Sektions-Slots in fester Reihenfolge (Vorplan §2).
 *
 * Die Shell hält `activeSection` und je einen Wrapper-ref pro Sektion (für
 * Scroll-To). Sie reicht jeder Sektion `ProfileSectionProps` durch und bleibt
 * stabil, wenn SP5/SP6 die Stub-Sektionen gegen echte Implementierungen
 * tauschen — der Vertrag liegt in `profile/profileShell.ts`.
 */

/** Statische Zuordnung SectionId → Sektions-Komponente (Reihenfolge via PROFILE_SECTIONS). */
const SECTION_COMPONENTS: Record<ProfileSectionId, React.FC<ProfileSectionProps>> = {
  stammdaten: ProfileStammdaten,
  benachrichtigungen: ProfileNotifications,
  termine: ProfileTermine,
  abwesenheiten: ProfileAbwesenheiten,
};

export const ProfilePage: React.FC = () => {
  const { isPhone } = useResponsive();
  const [activeSection, setActiveSection] = React.useState<ProfileSectionId>("stammdaten");

  // Ein stabiler Wrapper-ref pro Sektion (Scroll-Ziel).
  const refs = React.useRef<Record<ProfileSectionId, React.RefObject<HTMLDivElement>>>({
    stammdaten: React.createRef<HTMLDivElement>(),
    benachrichtigungen: React.createRef<HTMLDivElement>(),
    termine: React.createRef<HTMLDivElement>(),
    abwesenheiten: React.createRef<HTMLDivElement>(),
  });

  const onSelect = React.useCallback((id: ProfileSectionId) => {
    setActiveSection(id);
    refs.current[id].current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 860,
        margin: "0 auto",
        padding: isPhone ? 16 : 32,
        boxSizing: "border-box",
        fontFamily: REFINED.fontUI,
      }}
    >
      <ProfileHeader />

      <ProfileTabs items={PROFILE_SECTIONS} activeSection={activeSection} onSelect={onSelect} />

      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {PROFILE_SECTIONS.map((item) => {
          const Section = SECTION_COMPONENTS[item.id];
          const sectionRef = refs.current[item.id];
          return (
            // Wrapper-div trägt den Scroll-ref (scroll-margin-top sorgt dafür,
            // dass die sticky Tab-Leiste die Sektionsüberschrift nicht verdeckt).
            <div key={item.id} ref={sectionRef} style={{ scrollMarginTop: 64 }}>
              <Section sectionRef={sectionRef} activeSection={activeSection} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProfilePage;
